import { ToggleButton, ToggleButtonGroup, Box, Stack, Button } from '@mui/material';
import React, { useEffect, useState } from 'react';
import useBundleTranslation from 'i18n';
import {
    IntervalChartChartingInterval,
    MetricChartChartingInterval,
    MetricChartStateRange,
    MetricChartViewsMode,
} from 'components/metric/chart';
import { MetricChartTools } from 'components/metric/chart/metricChartTools';
import { ElementDisplayContextType } from 'components/metric/chart/MetricChartWrapper';
import { Chart as HChart } from 'highcharts';
import moment from 'moment';
import IconMi from 'components/common/icon/IconMi';
import { useEmbeddingContextVisible } from 'hooks/useEmbeddingContext';
import ChartCalendar from './ChartCalendar';

export default function MetricChartTopBar({
    views,
    actualView,
    onViewChange,
    chartingIntervals,
    selectedInterval,
    maxXValue,
    minXValue,
    setSelectedInterval,
    elementDisplayType = 'viewer',
    chart,
    measurementIntervalUnit,
    isSummaryVisible,
    onToggleSummaryVisibility,
}: {
    views: Array<MetricChartViewsMode>;
    actualView: MetricChartViewsMode;
    onViewChange: (view: MetricChartViewsMode) => void;
    chartingIntervals: Array<MetricChartChartingInterval>;
    selectedInterval?: MetricChartStateRange | null;
    setSelectedInterval: (interval: MetricChartChartingInterval) => void;
    measurementIntervalUnit: string;
    maxXValue: number;
    minXValue: number;
    elementDisplayType?: ElementDisplayContextType;
    chart: HChart;
    isSummaryVisible?: boolean;
    onToggleSummaryVisibility?: () => void;
}) {
    const { t } = useBundleTranslation(['components/metric/chart']);

    const allowedRanges: Array<IntervalChartChartingInterval> = chartingIntervals.filter((interval) => {
        if (maxXValue == 0 || interval.chartingIntervalUnit == 'custom') {
            return false;
        }
        return (
            MetricChartTools.calcChartingIntervalRange(
                maxXValue,
                interval.chartingIntervalUnit,
                -interval.chartingIntervalValue,
            ) >= minXValue
        );
    }) as Array<IntervalChartChartingInterval>;

    const [selectedIntervalId, setSelectedIntervalId] = useState<number>(0);

    useEffect(() => {
        if (!selectedInterval) {
            return;
        }

        if (selectedInterval.interval_unit == 'custom') {
            // TODO: add calendar
            return;
        }

        const interval = chartingIntervals.find(
            (i) =>
                i.chartingIntervalUnit != 'custom' &&
                i.chartingIntervalValue == selectedInterval.interval_value &&
                i.chartingIntervalUnit == selectedInterval.interval_unit,
        ) as IntervalChartChartingInterval | undefined;
        if (!interval) {
            return;
        }
        setSelectedIntervalId(interval.chartingIntervalId);
    }, [selectedInterval]);

    const handleChange = (event: React.MouseEvent<HTMLElement> | null, intervalId: number) => {
        const interval = chartingIntervals.find(
            (i) => i.chartingIntervalUnit != 'custom' && i.chartingIntervalId == intervalId,
        );
        if (!interval) {
            return false;
        }
        setSelectedInterval(interval);
    };

    const handleCalendarApply = (startDate: Date, endDate: Date) => {
        setIsCalendarVisible(false);
        setSelectedInterval({
            chartingIntervalUnit: 'custom',
            minValue: moment(startDate).unix() * 1000,
            maxValue: moment(endDate).unix() * 1000,
        });
    };

    if (elementDisplayType == 'screenshot') {
        return null;
    }

    const [isCalendarVisible, setIsCalendarVisible] = useState(false);
    const hasTime = ['minute', 'hour'].includes(measurementIntervalUnit);
    const hasSummary = useEmbeddingContextVisible('summary');

    return (
        <Stack
            direction="row"
            justifyContent="space-between"
            sx={{
                mb: 2,
                position: 'relative',
                '@container chartContainer (width < 900px)': {
                    '&': {
                        flexWrap: 'wrap',
                    },
                },
            }}
        >
            {elementDisplayType == 'viewer' && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        '@container chartContainer (width < 900px)': {
                            '&': {
                                position: 'static',
                                width: '100%',
                                order: 100,
                                overflow: 'auto',
                                justifyContent: 'normal',
                            },
                        },
                    }}
                >
                    <ToggleButtonGroup
                        sx={{
                            '@container chartContainer (width < 900px)': {
                                '&': {
                                    margin: '0 auto',
                                },
                            },
                        }}
                        color="primary"
                        value={actualView}
                        exclusive
                        onChange={(event, v) => {
                            if (v !== null) onViewChange(v);
                        }}
                    >
                        {views.map((v) => (
                            <ToggleButton value={v} key={v}>
                                {t(`view.${v}`)}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
            )}
            <Stack
                direction="row"
                sx={{
                    position: 'relative',
                    '@container chartContainer (width < 900px)': {
                        '&': {
                            mb: 1.5,
                        },
                    },
                }}
            >
                <ToggleButtonGroup color="primary" value={selectedIntervalId} exclusive onChange={handleChange}>
                    {allowedRanges.map((interval) => {
                        return (
                            <ToggleButton value={interval.chartingIntervalId} key={interval.chartingIntervalId}>
                                {interval.chartingIntervalValue + interval.chartingIntervalUnit[0]}
                            </ToggleButton>
                        );
                    })}
                </ToggleButtonGroup>
                <ChartCalendar
                    chart={chart}
                    hasTime={hasTime}
                    isCalendarVisible={isCalendarVisible}
                    onOpen={() => setIsCalendarVisible(true)}
                    onClose={() => setIsCalendarVisible(false)}
                    onApply={handleCalendarApply}
                />
            </Stack>
            {hasSummary && isSummaryVisible === false && (
                <Button
                    sx={{
                        position: 'relative',
                        '@container chartContainer (width < 900px)': {
                            '&': {
                                mb: 1.5,
                            },
                        },
                    }}
                    variant={'outlined'}
                    startIcon={<IconMi icon="chevron-left" />}
                    onClick={() => {
                        if (onToggleSummaryVisibility) onToggleSummaryVisibility();
                    }}
                >
                    {t('show_summery_btn')}
                </Button>
            )}
        </Stack>
    );
}
